import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  ShieldCheck,
  BookOpen,
  ClipboardList,
  Cookie,
  GraduationCap,
  Camera,
  UserRound,
  CakeSlice,
  Palette,
  ChevronLeft,
} from "lucide-react";

// Clean corridor photograph — no baked cards. Approved reference for the Main Hall.
const HALL_BACKGROUND =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_4fdfd6f9-6803-4276-8258-f36ae827b1f3/artifacts/dxtppvdh_E4022CB6-1425-43B8-8529-7DE68807CEB2.png";

// Eight top-level products in the approved four-row Hall composition.
// NOTE: "Открытая библиотека" intentionally has no subtitle.
const CARDS = [
  { id: "programs",     path: "/programs",     title: "Программы Anokina Studio",  subtitle: "Обучение",                        Icon: GraduationCap },
  { id: "account",      path: "/account",      title: "Мой кабинет",               subtitle: "Личное пространство",             Icon: UserRound },
  { id: "master-order", path: "/master-order", title: "Мастер заказа",             subtitle: "Профессиональный инструмент",     Icon: ClipboardList },
  { id: "brand-studio", path: "/brand-studio", title: "Brand Studio",              subtitle: "Твоя дизайн-мастерская",          Icon: Palette },
  { id: "open-library", path: "/library",      title: "Открытая библиотека",       subtitle: "Живая коллекция рецептов, историй и ответов", Icon: BookOpen },
  { id: "architect",    path: "/architect",    title: "Интеллект вкуса",       subtitle: "Интерактивный инструмент",        Icon: CakeSlice },
  { id: "media-studio", path: "/media-studio", title: "Медиа-студия",              subtitle: "Для соцсетей",                    Icon: Camera },
  { id: "gingerbread",  path: "/gingerbread", title: "Пряничная мастерская",      subtitle: "Творчество и ремесло",            Icon: Cookie },
];
const byId = (id) => CARDS.find((c) => c.id === id);

// Shared Hall surface/frame tokens. Radius is scaled to component height below,
// while material, border weight, inset and interaction states stay identical.
const HALL_SURFACE_CLASS =
  "hall-card group relative border border-[#4A3B32]/45 bg-[#F5EBD6]/94 hover:bg-[#FAF3E4] active:bg-[#F0E4CC] transition-colors shadow-[0_1px_2px_rgba(74,59,50,0.10)]";
const HALL_MAP_SURFACE_CLASS =
  "hall-card group relative border border-[#9A7657]/34 bg-[#FFF9EF]/97 hover:bg-[#FFFCF6] active:bg-[#F8EEDC] transition-colors shadow-[0_1px_2px_rgba(74,59,50,0.07)]";
const HALL_INNER_FRAME_CLASS = "absolute inset-[4px] pointer-events-none";
const HALL_INNER_FRAME_STYLE = {
  border: "1px solid rgba(74,59,50,0.28)",
  borderRadius: "var(--hall-overlay-radius)",
};
// On mobile the CTA and the card grid are one composition. The shared
// container owns their width so neither child can establish a competing
// intrinsic width. Desktop uses a compact fixed maximum for the eight-card grid.
const HALL_COMPOSITION_WIDTH_CLASS = "w-[min(82vw,250px)] sm:w-auto";

export default function MainHall() {
  const nav = useNavigate();
  const { user } = useAuth();
  const workspaceRole = ["owner", "administrator"].includes(user?.role) ? user.role : null;

  // Resolve the trial lifecycle once for the Hall banner. Owner gets the same
  // new-reader CTA, but it opens the existing mutation-free QA preview.
  // Other staff and permanent-access users stay outside trial messaging.
  const [trialAction, setTrialAction] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (["owner", "administrator"].includes(workspaceRole)) {
      setTrialAction({
        text: "Подать заявку на 24-часовое знакомство с Мастером заказа",
        path: "/master-order-testing?preview_state=application",
        state: "available",
      });
      return undefined;
    }
    if (workspaceRole) { setTrialAction(null); return undefined; }
    (async () => {
      try {
        const [access, application, intake] = await Promise.all([
          api.get("/master-order/access-mode").catch(() => ({ data: { mode: "no_access" } })),
          api.get("/master-order-testing/applications/mine").catch(() => ({ data: null })),
          api.get("/master-order-testing/intake-status").catch(() => ({ data: { open: true } })),
        ]);
        if (cancelled) return;
        const mode = access?.data?.mode || "no_access";
        const app = application?.data;
        let next = null;
        // Owner/staff and permanent-access users stay outside trial messaging.
        if (mode === "internal" || (mode === "full" && app?.status !== "active_test")) {
          setTrialAction(null);
          return;
        }
        if (app?.status === "new") {
          next = { text: "Заявка на рассмотрении", path: "/master-order-testing", state: "pending" };
        } else if (app?.status === "accepted") {
          next = { text: "Начать знакомство с Мастером заказа", path: "/master-order-testing", state: "approved" };
        } else if (app?.status === "active_test") {
          next = { text: "Продолжить работу в Мастере заказа", path: "/master-order", state: "active" };
        } else if (app?.status === "completed_test" || app?.status === "feedback_received") {
          next = { text: "24-часовое знакомство завершено", path: "/master-order-testing", state: "completed" };
        } else if (app?.status === "declined") {
          next = { text: "Заявка не одобрена", path: "/master-order-testing", state: "declined" };
        } else if (mode === "no_access" && intake?.data?.open !== false) {
          next = {
            text: "Подать заявку на 24-часовое знакомство с Мастером заказа",
            path: "/master-order-testing",
            state: "available",
          };
        }
        setTrialAction(next);
      } catch {
        if (!cancelled) setTrialAction(null);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceRole]);

  // We keep the corridor as a full-screen backdrop and place staff pills
  // as light overlays in the very top-right area — they never collide with cards
  // because cards start well below the top 6% of the frame.
  return (
    <div
      className="fixed inset-0 h-full w-full overflow-x-hidden overflow-y-auto"
      style={{ background: "#e8dcc4" }}
      data-testid="main-hall-page"
    >
      {/* Backdrop */}
      <img
        src={HALL_BACKGROUND}
        alt=""
        aria-hidden="true"
        className="fixed inset-0 h-full w-full object-cover"
        style={{ objectPosition: "50% 40%" }}
        fetchpriority="high"
        loading="eager"
        decoding="async"
      />
      {/* Soft warm veil for legibility on colored zones */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,246,232,0.12) 0%, rgba(255,246,232,0) 30%, rgba(0,0,0,0) 70%, rgba(74,59,50,0.10) 100%)",
        }}
      />

      {/* Entrance navigation only — authentication remains active. */}
      <button
        type="button"
        onClick={() => nav("/")}
        data-testid="back-to-door-button"
        aria-label="К входной двери"
        title="К входной двери"
        className="absolute z-30 top-3 left-3 md:top-5 md:left-5 min-h-[40px] px-3.5 rounded-lg bg-[#F5EBD6]/95 backdrop-blur border border-[#4A3B32]/55 flex items-center gap-2 hover:bg-[#FAF3E4] transition-colors shadow-[0_2px_10px_rgba(74,59,50,0.16)]"
      >
        <ChevronLeft className="w-4 h-4 text-[#4A3B32] shrink-0" strokeWidth={1.6} />
        <span className="font-serif text-[13px] text-[#3A2B22] whitespace-nowrap">
          К входной двери
        </span>
      </button>

      {/* Owner/Administrator control only. */}
      {workspaceRole && (
        <button
          onClick={() => nav("/admin")}
          data-testid="admin-workspace-entry"
          className="absolute z-30 top-3 right-3 md:top-5 md:right-5 min-h-[40px] rounded-lg bg-white/88 backdrop-blur border border-[#4A3B32]/40 flex items-center gap-1.5 px-3 md:px-4 hover:bg-white transition-colors shadow-[0_2px_10px_rgba(74,59,50,0.14)]"
          title="Панель управления"
        >
          <ShieldCheck className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#4A3B32] shrink-0" />
          <span className="font-serif text-[12px] md:text-sm text-[#4A3B32] whitespace-nowrap">
            Панель управления
          </span>
        </button>
      )}
      {/* Trial lifecycle banner + the approved four-by-two product grid. */}
      <div className="relative z-10 flex min-h-full w-full items-center justify-center px-6 pb-6 pt-16 sm:px-8 sm:pt-20">
        <div className={`mx-auto box-border flex flex-col gap-[10px] sm:gap-3 md:gap-[10px] ${HALL_COMPOSITION_WIDTH_CLASS}`}>
          <button
            type="button"
            onClick={() => nav("/professional-map")}
            data-testid="hall-professional-map-banner"
            className={`${HALL_MAP_SURFACE_CLASS} hall-card--compact box-border w-full min-h-[58px] sm:h-[54px] px-4 flex items-center justify-center`}
            style={{ backdropFilter: "blur(2px)" }}
          >
            <span
              aria-hidden="true"
              className={HALL_INNER_FRAME_CLASS}
              style={HALL_INNER_FRAME_STYLE}
            />
            <span className="relative w-full min-w-0 flex flex-col items-center justify-center text-center">
              <span className="font-serif text-[#3A2B22] text-[10.5px] sm:text-[13px] md:text-[14px] leading-[1.15] text-center">
                <span className="sm:hidden flex flex-col items-center whitespace-nowrap">
                  <span>Профессиональная карта</span>
                  <span>кондитерской индустрии</span>
                </span>
                <span className="hidden sm:inline">
                  Профессиональная карта кондитерской индустрии
                </span>
              </span>
              <span className="mt-1 text-[6.4px] sm:text-[7.5px] md:text-[8px] tracking-[0.07em] sm:tracking-[0.10em] uppercase text-[#806B5B] leading-none whitespace-nowrap">
                Мастера · Поставщики · Услуги · Вакансии · Запросы
              </span>
            </span>
          </button>

          {trialAction && (
            <button
              type="button"
              onClick={() => nav(trialAction.path)}
              data-testid="hall-mo-trial-banner"
              data-trial-state={trialAction.state}
              className={`${HALL_SURFACE_CLASS} hall-card--compact box-border w-full h-[52px] sm:h-11 px-4 flex items-center justify-center`}
              style={{ backdropFilter: "blur(2px)" }}
            >
              <span
                aria-hidden="true"
                className={HALL_INNER_FRAME_CLASS}
                style={HALL_INNER_FRAME_STYLE}
              />
              <span className="relative font-serif text-[#3A2B22] text-[10.5px] sm:text-[13px] md:text-[14px] leading-[1.2] sm:leading-[1.12] text-center">
                {trialAction.state === "available" ? (
                  <>
                    <span className="sm:hidden flex flex-col items-center whitespace-nowrap">
                      <span>Подать заявку на 24-часовое</span>
                      <span>знакомство с Мастером заказа</span>
                    </span>
                    <span className="hidden sm:inline">{trialAction.text}</span>
                  </>
                ) : trialAction.text}
              </span>
            </button>
          )}
          <div
            className="mx-auto box-border grid grid-cols-2 gap-[10px] sm:gap-3 md:gap-[10px] w-full sm:w-auto sm:max-w-[520px] md:max-w-[500px] lg:max-w-[500px]"
            data-testid="main-hall-grid"
          >
            <HallCard card={byId("programs")} />
            <HallCard card={byId("account")} />
            <HallCard card={byId("master-order")} pathOverride={!workspaceRole && trialAction?.state !== "active" ? trialAction?.path : undefined} />
            <HallCard card={byId("brand-studio")} />
            <HallCard card={byId("open-library")} />
            <HallCard card={byId("architect")} />
            <HallCard card={byId("media-studio")} />
            <HallCard card={byId("gingerbread")} />
          </div>
        </div>
      </div>
    </div>
  );
}

function HallCard({ card, wide, pathOverride }) {
  const nav = useNavigate();
  if (!card) return null;
  const { id, path, title, subtitle, Icon } = card;
  // Paired cards are aspect-square (1:1). Architect (wide) spans both columns
  // and uses a reduced height ~45% of its width via aspect-[2.2/1].
  return (
    <button
      type="button"
      onClick={() => nav(pathOverride || path)}
      data-testid={`hall-space-${id}`}
      className={`${HALL_SURFACE_CLASS} flex flex-col items-center justify-center text-center rounded-2xl px-2 ${
        wide ? "col-span-2 aspect-[2.5/1] md:aspect-[3.4/1] py-1.5" : "aspect-square md:aspect-[1.5/1] py-1.5"
      }`}
      style={{ backdropFilter: "blur(2px)" }}
    >
      {/* Inner hairline — double-border feel */}
      <span
        aria-hidden="true"
        className={HALL_INNER_FRAME_CLASS}
        style={HALL_INNER_FRAME_STYLE}
      />
      {Icon && (
        <span
          className="mb-0.5 sm:mb-1 inline-flex items-center justify-center rounded-full border border-[#4A3B32]/45 bg-white/40 w-[26px] h-[26px] sm:w-8 sm:h-8"
          aria-hidden="true"
        >
          <Icon
            className="text-[#4A3B32] w-[12px] h-[12px] sm:w-[14px] sm:h-[14px]"
            strokeWidth={1.35}
          />
        </span>
      )}
      <div className="font-serif text-[#3A2B22] text-[11px] sm:text-[13px] md:text-[14px] leading-[1.12] px-0.5">
        {title}
      </div>
      {subtitle && (
        <div className="mt-0.5 text-[8px] sm:text-[10px] tracking-[0.09em] uppercase text-[#7d6a5c] leading-tight px-0.5">
          {subtitle}
        </div>
      )}
    </button>
  );
}
